import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import sanitizeHtml, { IOptions } from 'sanitize-html';

import { SPINNER_MOTION } from '@/components/ui/spinner';
import { AegisManagementStatus } from '@/hooks/useAegisManagement';
import { cn } from '@/utils/classname';

/**
 * Get the appropriate status icon for an Aegis task status
 * @param status - The task status string
 * @returns React icon component for the status
 */
export const getAegisTaskStatusIcon = (status: string): React.ReactNode => {
  switch (status) {
    case AegisManagementStatus.COMPLETED:
      return <CheckCircleIcon className="text-chariot-status-info size-4" />;
    case AegisManagementStatus.FAILED:
      return <XCircleIcon className="text-chariot-status-critical size-4" />;
    case AegisManagementStatus.RUNNING:
      return (
        <ArrowPathIcon
          className={cn('text-chariot-status-medium size-4', SPINNER_MOTION)}
        />
      );
    case AegisManagementStatus.PENDING:
      return <ArrowPathIcon className="text-chariot-text-secondary size-4" />;
    default:
      return <ArrowPathIcon className="text-chariot-text-secondary size-4" />;
  }
};

/**
 * Get a human-readable label for an Aegis task status
 * @param status - The task status string
 * @returns Human-readable status label
 */
export const getAegisTaskStatusLabel = (status: string): string => {
  switch (status) {
    case AegisManagementStatus.COMPLETED:
      return 'Completed successfully';
    case AegisManagementStatus.FAILED:
      return 'Failed with errors';
    case AegisManagementStatus.RUNNING:
      return 'In progress';
    case AegisManagementStatus.PENDING:
      return 'Waiting to start';
    default:
      return status;
  }
};

/**
 * Get a human-readable description for an Aegis management capability
 * @param capability - The capability identifier string
 * @returns Human-readable capability description
 */
export const getAegisCapabilityDescription = (capability: string): string => {
  const descriptions: Record<string, string> = {
    'linux-cloudflared-install': 'Install Cloudflare Tunnel',
    'linux-cloudflared-remove': 'Remove Cloudflare Tunnel',
    'linux-system-adduser': 'Add System User',
    'linux-system-deluser': 'Delete System User',
  };
  return descriptions[capability] || capability;
};

/**
 * Check if a task status is considered active/in-progress
 * @param status - The task status string
 * @returns True if the task is pending or running
 */
export const isAegisTaskActive = (status: string): boolean => {
  return (
    status === AegisManagementStatus.PENDING ||
    status === AegisManagementStatus.RUNNING
  );
};

/**
 * Format task parameters for display
 * @param parameters - Record of task parameters
 * @returns Formatted parameter string
 */
export const formatAegisTaskParameters = (
  parameters: Record<string, string>
): string => {
  return Object.entries(parameters)
    .filter(([_, value]) => value && value.trim() !== '')
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

/**
 * Get user-friendly error message with recovery guidance
 * @param error - Error message or object
 * @param capability - The capability that failed (optional)
 * @returns Object with user message and recovery steps
 */
export const getAegisErrorGuidance = (
  error: string,
  capability?: string
): {
  message: string;
  recoverySteps: string[];
} => {
  const lowerError = error.toLowerCase();

  if (lowerError.includes('tunnel') || capability?.includes('cloudflared')) {
    return {
      message: 'Cloudflare tunnel operation failed',
      recoverySteps: [
        'Verify the endpoint is online and accessible',
        'Check Cloudflare API credentials are configured',
        'Ensure sufficient permissions for tunnel operations',
        'Try refreshing the tasks list and retry the operation',
      ],
    };
  }

  if (lowerError.includes('user') || capability?.includes('user')) {
    return {
      message: 'User management operation failed',
      recoverySteps: [
        'Verify the endpoint has sufficient system permissions',
        'Check that the username follows Linux naming conventions',
        'Ensure the Cloudflare tunnel is configured for SSH access',
        'Verify the endpoint is online and responsive',
      ],
    };
  }

  if (lowerError.includes('timeout')) {
    return {
      message: 'Operation timed out',
      recoverySteps: [
        'Check endpoint connectivity and responsiveness',
        "Verify the operation isn't blocked by system policies",
        'Try the operation again after a brief wait',
        'Contact support if timeouts persist',
      ],
    };
  }

  return {
    message: error || 'An unexpected error occurred',
    recoverySteps: [
      'Verify the endpoint is online and accessible',
      'Check your network connection',
      'Refresh the page and try again',
      'Contact support if the issue persists',
    ],
  };
};

/**
 * Get status-specific guidance for task completion
 * @param status - The task status
 * @param capability - The capability name
 * @returns Guidance message for the user
 */
export const getAegisTaskGuidance = (
  status: string,
  capability: string
): string => {
  if (status === AegisManagementStatus.RUNNING) {
    if (capability.includes('tunnel')) {
      return 'Setting up Cloudflare tunnel. This may take a few minutes.';
    }
    if (capability.includes('user')) {
      return 'Managing user account. Please wait for completion.';
    }
    return 'Operation in progress. Please wait for completion.';
  }

  return '';
};

/**
 * Sanitize task error messages to prevent XSS attacks
 *
 * @param errorMessage - Raw error message that may contain unsafe content
 * @returns Sanitized error message safe for display
 */
export const sanitizeTaskErrorMessage = (errorMessage: string): string => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return '';
  }

  // Configure sanitization options - very restrictive for security
  const sanitizeOptions: IOptions = {
    allowedTags: [], // No HTML tags allowed in error messages
    allowedAttributes: {},
    allowedIframeHostnames: [],
    disallowedTagsMode: 'discard',
    enforceHtmlBoundary: false,
  };

  return sanitizeHtml(errorMessage, sanitizeOptions).trim();
};

/**
 * Sanitize and format task error message for display
 *
 * @param errorMessage - Raw error message
 * @param maxLength - Maximum length for the error message (default: 500)
 * @returns Safe, formatted error message
 */
export const formatSafeTaskErrorMessage = (
  errorMessage: string,
  maxLength: number = 500
): string => {
  const sanitized = sanitizeTaskErrorMessage(errorMessage);

  if (!sanitized) {
    return 'An error occurred but no details are available.';
  }

  // Truncate if too long
  if (sanitized.length > maxLength) {
    return sanitized.substring(0, maxLength - 3) + '...';
  }

  return sanitized;
};
